import React, { useState } from 'react';
import {
  HideImage as HideImageIcon,
  Verified as VerifiedIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useProductContext } from '../../../context/ProductContext';
import { useShopContext } from '../../../context/ShopContext';
import { BranchProduct, BranchProductAssignInput } from '../../../types';
import ProductFormModal from './ProductFormModal';
import ConfirmDialog from '../ConfirmDialog';
import AddImagesModal from './AddImagesModal';
import CustomProductTooltip from './CustomProductTooltip';

interface ProductTableProps {
  products: BranchProduct[];
  isLoading: boolean;
  error: string | null;
}

const ProductTable: React.FC<ProductTableProps> = ({ products, isLoading, error }) => {
  const { updateProduct, deleteBranchProduct, assignProductToBranch, categories } = useProductContext();
  const { adminBranches } = useShopContext();
  const [sortConfig, setSortConfig] = useState<{ key: keyof BranchProduct | string; direction: 'asc' | 'desc' } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editProduct, setEditProduct] = useState<BranchProduct | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [addImagesProduct, setAddImagesProduct] = useState<{ id: number; name: string } | null>(null);
  const [isAddImagesModalOpen, setIsAddImagesModalOpen] = useState(false);
  const [previewProductId, setPreviewProductId] = useState<number | null>(null);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const handleSort = (key: keyof BranchProduct | string) => {
    setSortConfig((prev) => {
      if (prev?.key === key && prev.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const sortedProducts = [...products].sort((a, b) => {
    if (!sortConfig) return 0;
    let aValue: any;
    let bValue: any;
    if (sortConfig.key === 'product.name') {
      aValue = a.product.name.toLowerCase();
      bValue = b.product.name.toLowerCase();
    } else if (sortConfig.key === 'product.sku') {
      aValue = a.product.sku.toLowerCase();
      bValue = b.product.sku.toLowerCase();
    } else if (sortConfig.key === 'branch.name') {
      aValue = a.branch.name.toLowerCase();
      bValue = b.branch.name.toLowerCase();
    } else {
      aValue = a[sortConfig.key as keyof BranchProduct];
      bValue = b[sortConfig.key as keyof BranchProduct];
    }
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const filteredProducts = sortedProducts.filter(
    (product) =>
      product.product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.product.sku.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleEdit = (product: BranchProduct) => {
    setEditProduct(product);
    setIsModalOpen(true);
  };

  const handleDelete = (branchProductId: number) => {
    setDeleteId(branchProductId);
    setIsConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (deleteId) {
      try {
        await deleteBranchProduct(deleteId);
      } catch {}
      setIsConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const handleAddImages = (productId: number, productName: string) => {
    setAddImagesProduct({ id: productId, name: productName });
    setIsAddImagesModalOpen(true);
  };

  const handleEditSubmit = async (productData: FormData, assignData: BranchProductAssignInput) => {
    if (!editProduct) return;
    try {
      await updateProduct(editProduct.product.id, productData);
      if (
        assignData.branch_id !== editProduct.branch.id ||
        assignData.price !== editProduct.price ||
        assignData.stock !== editProduct.stock ||
        assignData.is_available !== editProduct.is_available
      ) {
        await deleteBranchProduct(editProduct.id);
        await assignProductToBranch({
          ...assignData,
          product_id: editProduct.product.id,
        });
      }
    } catch {}
    setIsModalOpen(false);
    setEditProduct(null);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <input
        type="text"
        placeholder="Search by name or SKU..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="w-full max-w-xs border border-gray-300 rounded-md px-3 py-2 mb-4"
      />

      <div className="overflow-x-auto rounded-lg">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-gray-100 font-semibold text-gray-600 text-xs uppercase">
            <tr>
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3">SKU</th>
              <th className="px-4 py-3">Price</th>
              <th className="px-4 py-3">Stock</th>
              <th className="px-4 py-3">Branch</th>
              <th className="px-4 py-3">Available</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredProducts.map((product) => (
              <tr key={`${product.product.id}-${product.branch.id}`} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 relative">
                    {product.product.images?.[0]?.image ? (
                      <img
                        src={product.product.images[0].image}
                        alt={product.product.name}
                        className="w-12 h-12 rounded object-cover border"
                      />
                    ) : (
                      <div className="w-12 h-12 flex items-center justify-center bg-gray-100 border rounded">
                        <HideImageIcon className="text-gray-400" />
                      </div>
                    )}

                    {!isMobile && (
                      <span
                        className={`text-sm font-medium flex items-center gap-1 px-2 py-1 rounded-full ${
                          product.is_available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {product.is_available ? <VerifiedIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                        {product.product.name}
                      </span>
                    )}

                    {isMobile && (
                      <button
                        onClick={() =>
                          setPreviewProductId((prev) =>
                            prev === product.product.id ? null : product.product.id
                          )
                        }
                        className="p-1 rounded-full text-gray-600 hover:text-primary focus:outline-none"
                        aria-label={`Preview ${product.product.name}`}
                      >
                        <VisibilityIcon fontSize="small" />
                      </button>
                    )}

                    {isMobile && previewProductId === product.product.id && (
                      <div className="absolute top-14 left-0 z-50">
                        <CustomProductTooltip
                          name={product.product.name}
                          price={product.price}
                          stock={product.stock}
                          branchName={product.branch.name}
                          isAvailable={product.is_available}
                          isOpen
                          onClose={() => setPreviewProductId(null)}
                        />
                      </div>
                    )}
                  </div>
                </td>

                <td className="px-4 py-3">{product.product.sku}</td>
                <td className="px-4 py-3">${product.price}</td>
                <td className="px-4 py-3">{product.stock}</td>
                <td className="px-4 py-3">{product.branch.name}</td>
                <td className="px-4 py-3">{product.is_available ? 'Yes' : 'No'}</td>
                <td className="px-4 py-3 space-x-2">
                  <button onClick={() => handleEdit(product)} className="text-blue-600 hover:underline">Edit</button>
                  <button onClick={() => handleAddImages(product.product.id, product.product.name)} className="text-green-600 hover:underline">Images</button>
                  <button onClick={() => handleDelete(product.id)} className="text-red-600 hover:underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editProduct && (
        <ProductFormModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditProduct(null);
          }}
          onSubmit={handleEditSubmit}
          categories={categories}
          branches={adminBranches}
          isLoading={isLoading}
          initialData={{
            name: editProduct.product.name,
            sku: editProduct.product.sku,
            description: editProduct.product.description || '',
            short_description: editProduct.product.description || '',
            base_price: editProduct.product.base_price,
            category_ids: editProduct.product.categories?.map((c) => c.id) || [],
            is_featured: editProduct.product.is_featured,
            badge: editProduct.product.badge || '',
          }}
          initialBranchData={{
            branchId: editProduct.branch.id,
            branchPrice: editProduct.price,
            stock: editProduct.stock,
            isAvailable: editProduct.is_available,
          }}
        />
      )}

      <ConfirmDialog
        isOpen={isConfirmOpen}
        title="Delete Product"
        message="Are you sure you want to remove this product?"
        confirmText="Delete"
        onConfirm={confirmDelete}
        onCancel={() => setIsConfirmOpen(false)}
      />

      {addImagesProduct && (
        <AddImagesModal
          isOpen={isAddImagesModalOpen}
          onClose={() => {
            setIsAddImagesModalOpen(false);
            setAddImagesProduct(null);
          }}
          productId={addImagesProduct.id}
          productName={addImagesProduct.name}
        />
      )}
    </div>
  );
};

export default ProductTable;
